import React, { useState, useEffect } from 'react'
import {
  Search, Download, User,
  FileText, CheckCircle, Plus, Edit2, Trash2,
  RefreshCw, Award, Gift, ShoppingCart,
  Calendar, Settings, ToggleLeft, Users,
  Gavel, UtensilsCrossed, Shield, Zap,
  Globe, Smartphone, Monitor, Cog, Code,
  Lock, Unlock, Warehouse
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO, subDays, isToday, isYesterday } from 'date-fns'

interface AuditLog {
  id: string
  user_id: string
  user_name: string
  action: string
  entity_type: string
  entity_id?: string
  details?: any
  ip_address?: string
  source?: string | null
  created_at: string
}

// --- Category mapping ---

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'chores', label: 'Chores' },
  { value: 'points', label: 'Points' },
  { value: 'events', label: 'Events' },
  { value: 'system', label: 'System' },
  { value: 'devices', label: 'Devices' },
]

// --- Sentence templates ---

const TEMPLATES: Record<string, string> = {
  // Assignments - kid context included
  'assignment.created': '{chore_name} assigned to {kid_name}',
  'assignment.claimed': '{kid_name} claimed {chore_name}',
  'assignment.completed': '{kid_name} completed {chore_name}',
  'assignment.verified': '{kid_name}\'s {chore_name} verified \u2014 {points_awarded} pts',
  'assignment.rejected': '{kid_name}\'s {chore_name} rejected',
  'assignment.unverified': '{kid_name}\'s {chore_name} unverified \u2014 {points_reversed} pts reversed',
  'assignment.parent_completed': '{kid_name}\'s {chore_name} completed by parent',
  'assignment.updated': '{kid_name}\'s {chore_name} updated',
  'assignment.bid': '{kid_name} bid {bid_points} pts on {chore_name}',
  'assignment.scheduled': '{chore_name} assigned to {kid_name}',
  'assignment.reassigned': '{chore_name} reassigned to {new_kid_name}',
  'assignment.event_opened': 'Chores opened for event',
  'assignment.event_restored': 'Chores restored after event change',
  'assignment.deleted': '{kid_name}\'s {chore_name} deleted',
  'assignment.daily_generated': '{generated} assignments generated for {date}',
  'assignment.week_generated': '{total_generated} assignments generated for the week',
  'assignment.photo_uploaded': 'Photo uploaded for {chore_name}',
  // Points - kid context included
  'points.awarded': '{amount} pts awarded to {kid_name} for {chore_name}',
  'points.reversed': '{amount} pts reversed from {kid_name} for {chore_name}',
  'points.adjusted': '{amount} pts adjusted for {kid_name} on {chore_name}',
  'points.deducted': '{amount} pts deducted from {target_name}: {reason}',
  'points.redeemed': '{reward_name} redeemed for {amount} pts',
  // Rewards
  'reward.created': 'Reward "{name}" created ({cost_points} pts)',
  'reward.updated': 'Reward updated',
  'reward.deleted': 'Reward "{name}" deleted',
  'reward.approved': 'Reward redemption approved',
  'reward.rejected': 'Reward redemption rejected',
  'reward.settings_updated': 'Reward settings updated',
  'reward.distributed': 'Points distributed to {recipient_count} members',
  'reward.fulfilled': 'Reward distribution fulfilled',
  'reward.monthly_distributed': 'Monthly rewards distributed',
  // Marketplace
  'marketplace.offer_created': 'Marketplace offer for {asking_points} pts',
  'marketplace.offer_accepted': 'Marketplace trade completed',
  'marketplace.offer_cancelled': 'Marketplace offer cancelled',
  'marketplace.bid_placed': 'Marketplace bid of {bid_amount} pts',
  'marketplace.trade_created': 'Trade offer created',
  'marketplace.trade_accepted': 'Trade accepted',
  'marketplace.trade_rejected': 'Trade rejected',
  'marketplace.chore_bid': 'Chore bid placed ({bid_points} pts)',
  'marketplace.chore_closed': 'Chore bidding closed',
  // Events
  'event.created': 'Event "{title}" created',
  'event.updated': 'Event updated',
  'event.deleted': 'Event deleted',
  'event.kid_request_created': 'Kid request: {title}',
  'event.kid_request_updated': 'Kid request updated',
  'event.rsvp': 'RSVP: {status}',
  'event.invite_sent': 'Invitations sent to {invited_count} people',
  // Availability & locations
  'availability.override_saved': 'Availability override for {override_date}',
  'availability.override_updated': 'Availability override updated for {override_date}',
  'availability.override_deleted': 'Availability override deleted',
  'location.created': 'Location "{name}" created',
  'location.updated': 'Location "{name}" updated',
  'location.deleted': 'Location deleted',
  // Rotation groups
  'rotation.created': 'Rotation group "{name}" created',
  'rotation.updated': 'Rotation group updated',
  'rotation.deleted': 'Rotation group deleted',
  'rotation.assigned': '{assignments_created} assignments created',
  // Settings & system
  'setting.updated': '{key} changed to {value}',
  'feature.toggled': '{feature_key} {enabled}',
  'user.created': '{username} added ({role})',
  'user.updated': 'User profile updated',
  'user.profile_updated': 'Profile updated',
  'user.deleted': 'User deleted',
  'user.account_deleted': 'Account deleted',
  'auction.resolved': '{chore_name} won by {winner_name} for {winning_points} pts',
  'chore.created': 'Chore "{name}" created',
  'chore.updated': 'Chore "{name}" updated',
  'chore.deleted': 'Chore "{name}" deleted',
  'dinner.attendance_changed': 'Dinner attendance updated',
  // Streaks
  'streak.bonus_awarded': '{kid_name} earned {bonus_points} pts streak bonus ({streak_count} day streak)',
  'streak.broken': '{kid_name}\'s {previous_streak} day streak ended',
  // Deductions
  'deduction.template_created': 'Deduction template "{reason}" created',
  'deduction.template_updated': 'Deduction templates updated',
  'deduction.template_deleted': 'Deduction template "{reason}" deleted',
  'deduction.acknowledged': 'Deduction acknowledged',
  'deduction.deleted': 'Deduction reversed',
  // Admin operations
  'recipe.created': 'Recipe "{name}" created',
  'recipe.updated': 'Recipe "{name}" updated',
  'recipe.deleted': 'Recipe deleted',
  'recipe.imported': 'Recipe imported from URL',
  'place.created': 'Place "{name}" created',
  'place.updated': 'Place "{name}" updated',
  'place.deleted': 'Place deleted',
  'holiday.created': 'Holiday "{name}" created',
  'holiday.updated': 'Holiday "{name}" updated',
  'holiday.deleted': 'Holiday deleted',
  'backup.created': 'Backup created',
  'backup.restored': 'Backup restored: {filename}',
  'backup.deleted': 'Backup deleted: {filename}',
  'backup.config_updated': 'Backup config updated',
  'calendar_source.created': 'Calendar source "{name}" added',
  'calendar_source.updated': 'Calendar source "{name}" updated',
  'calendar_source.deleted': 'Calendar source deleted',
  'calendar_source.synced': 'Calendar source synced',
  'calendar_events.imported': 'Calendar events imported',
  'meal.vendor_created': 'Meal vendor "{name}" created',
  'meal.vendor_updated': 'Meal vendor updated',
  'meal.vendor_deleted': 'Meal vendor deleted',
  'meal.order_placed': 'Meal order placed',
  'shopping.item_added': 'Shopping item "{name}" added',
  'shopping.item_toggled': 'Shopping item toggled',
  'integration.created': 'Integration "{name}" created',
  'integration.updated': 'Integration updated',
  'integration.deleted': 'Integration deleted',
  'integration.api_key_created': 'API key created for {name}',
  'integration.api_key_revoked': 'API key revoked',
  // Devices (pairing and shared devices from the API; door and garage from the kiosk)
  'device.paired': '{device_name} approved',
  'device.revoked': '{device_name} removed',
  'device.expired': '{device_name} trust expired',
  'device.session_started': 'Signed in on {device_name}',
  'device.step_up_requested': 'Parent access requested on {device_name}',
  'device.step_up_approved': 'Parent access approved for {device_name}',
  'device.step_up_denied': 'Parent access denied for {device_name}',
  'device.step_up_expired': 'Parent access request expired',
  'device.door_unlocked': 'Front door unlocked from {device_name}',
  'device.door_locked': 'Front door locked from {device_name}',
  'device.garage_opened': 'Garage opened from {device_name}',
  'device.garage_closed': 'Garage closed from {device_name}',
}

// Display metadata for the audit_logs.source column populated in v6.0.63+.
// Controller traffic auto-infers from the request conn (web / mobile / kiosk);
// system callers pass a literal (scheduler / effects_engine / auction_resolver /
// migration). Rows written before the source column existed have source=null.
const SOURCE_META: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  web:               { label: 'Web',       icon: <Globe className="w-3 h-3" />,      color: 'text-indigo-600', bg: 'bg-indigo-50' },
  mobile:            { label: 'Mobile',    icon: <Smartphone className="w-3 h-3" />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  kiosk:             { label: 'Kiosk',     icon: <Monitor className="w-3 h-3" />,    color: 'text-violet-600', bg: 'bg-violet-50' },
  api:               { label: 'API',       icon: <Code className="w-3 h-3" />,       color: 'text-slate-600', bg: 'bg-slate-100' },
  scheduler:         { label: 'Scheduler', icon: <Zap className="w-3 h-3" />,        color: 'text-amber-600', bg: 'bg-amber-50' },
  effects_engine:    { label: 'Effects',   icon: <Cog className="w-3 h-3" />,        color: 'text-cyan-600',  bg: 'bg-cyan-50' },
  auction_resolver:  { label: 'Auction',   icon: <Gavel className="w-3 h-3" />,      color: 'text-amber-600', bg: 'bg-amber-50' },
  migration:         { label: 'Migration', icon: <RefreshCw className="w-3 h-3" />,  color: 'text-gray-600',  bg: 'bg-gray-100' },
}

// Back-compat lookup for the legacy `details.source` values system callers used
// to embed before v6.0.63 added the real column. The actor fallback in
// getActorName/1 still reads these for older rows.
const SOURCE_NAMES: Record<string, string> = {
  rotation_scheduler: 'Scheduler',
  auction_resolver: 'Auction',
  event_effects: 'Event Rules',
}

const SOURCE_FILTERS = [
  { value: 'all', label: 'All Sources' },
  { value: 'web', label: 'Web' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'system', label: 'System' }, // groups scheduler/effects_engine/auction_resolver/migration/api
] as const

const SYSTEM_SOURCES = new Set(['scheduler', 'effects_engine', 'auction_resolver', 'migration', 'api'])

// --- Icon + color per action key ---

type ActionStyle = { icon: React.ReactNode; color: string; bg: string }

function getActionStyle(entityType: string, action: string): ActionStyle {
  const key = `${entityType}.${action}`
  switch (key) {
    case 'assignment.claimed':
      return { icon: <User className="w-4 h-4" />, color: 'text-indigo-600', bg: 'bg-indigo-50' }
    case 'assignment.completed':
    case 'assignment.parent_completed':
      return { icon: <CheckCircle className="w-4 h-4" />, color: 'text-green-600', bg: 'bg-green-50' }
    case 'assignment.verified':
      return { icon: <Shield className="w-4 h-4" />, color: 'text-blue-600', bg: 'bg-blue-50' }
    case 'assignment.unverified':
    case 'assignment.rejected':
      return { icon: <RefreshCw className="w-4 h-4" />, color: 'text-orange-600', bg: 'bg-orange-50' }
    case 'assignment.scheduled':
      return { icon: <Zap className="w-4 h-4" />, color: 'text-purple-600', bg: 'bg-purple-50' }
    case 'assignment.reassigned':
      return { icon: <RefreshCw className="w-4 h-4" />, color: 'text-purple-600', bg: 'bg-purple-50' }
    case 'assignment.deleted':
      return { icon: <Trash2 className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-50' }
    case 'assignment.bid':
      return { icon: <Gavel className="w-4 h-4" />, color: 'text-amber-600', bg: 'bg-amber-50' }
    case 'assignment.created':
      return { icon: <Plus className="w-4 h-4" />, color: 'text-green-600', bg: 'bg-green-50' }
    default:
      break
  }
  switch (entityType) {
    case 'points':
      return { icon: <Award className="w-4 h-4" />, color: 'text-amber-600', bg: 'bg-amber-50' }
    case 'marketplace':
      return { icon: <ShoppingCart className="w-4 h-4" />, color: 'text-teal-600', bg: 'bg-teal-50' }
    case 'event':
      return { icon: <Calendar className="w-4 h-4" />, color: 'text-blue-600', bg: 'bg-blue-50' }
    case 'availability':
      return { icon: <Calendar className="w-4 h-4" />, color: 'text-cyan-600', bg: 'bg-cyan-50' }
    case 'dinner':
      return { icon: <UtensilsCrossed className="w-4 h-4" />, color: 'text-orange-600', bg: 'bg-orange-50' }
    case 'setting':
      return { icon: <Settings className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-50' }
    case 'feature':
      return { icon: <ToggleLeft className="w-4 h-4" />, color: 'text-violet-600', bg: 'bg-violet-50' }
    case 'user':
      return { icon: <Users className="w-4 h-4" />, color: 'text-indigo-600', bg: 'bg-indigo-50' }
    case 'chore':
      return action === 'deleted'
        ? { icon: <Trash2 className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-50' }
        : { icon: <Edit2 className="w-4 h-4" />, color: 'text-blue-600', bg: 'bg-blue-50' }
    case 'auction':
      return { icon: <Gavel className="w-4 h-4" />, color: 'text-amber-600', bg: 'bg-amber-50' }
    case 'device':
      if (action === 'door_unlocked') return { icon: <Unlock className="w-4 h-4" />, color: 'text-red-600', bg: 'bg-red-50' }
      if (action === 'door_locked') return { icon: <Lock className="w-4 h-4" />, color: 'text-green-600', bg: 'bg-green-50' }
      if (action === 'garage_opened' || action === 'garage_closed') return { icon: <Warehouse className="w-4 h-4" />, color: 'text-amber-600', bg: 'bg-amber-50' }
      return { icon: <Monitor className="w-4 h-4" />, color: 'text-violet-600', bg: 'bg-violet-50' }
    default:
      return { icon: <Gift className="w-4 h-4" />, color: 'text-gray-600', bg: 'bg-gray-50' }
  }
}

// --- Helpers ---

function renderSentence(log: AuditLog): string {
  const key = `${log.entity_type}.${log.action}`
  const template = TEMPLATES[key]
  if (!template) return `${log.action} on ${log.entity_type}`

  const details = (typeof log.details === 'string' ? (() => { try { return JSON.parse(log.details) } catch { return {} } })() : log.details) || {}

  return template.replace(/\{(\w+)\}/g, (_match, field) => {
    const val = details[field]
    if (val === undefined || val === null) return ''
    if (field === 'enabled') return val === true || val === 'true' ? 'enabled' : 'disabled'
    return String(val)
  })
}

function getActorName(log: AuditLog): string {
  if (log.user_name) return log.user_name
  if (!log.user_id) {
    const details = (typeof log.details === 'string' ? (() => { try { return JSON.parse(log.details) } catch { return {} } })() : log.details) || {}
    return SOURCE_NAMES[details.source] || 'System'
  }
  return 'Unknown'
}

function getDateGroupLabel(dateStr: string): string {
  const date = parseISO(dateStr)
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'EEEE, MMMM d, yyyy')
}

function groupByDate(logs: AuditLog[]): { label: string; logs: AuditLog[] }[] {
  const groups: { label: string; logs: AuditLog[] }[] = []
  let currentLabel = ''

  for (const log of logs) {
    const label = getDateGroupLabel(log.created_at)
    if (label !== currentLabel) {
      currentLabel = label
      groups.push({ label, logs: [] })
    }
    groups[groups.length - 1].logs.push(log)
  }
  return groups
}

// --- Component ---

export const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [category, setCategory] = useState('all')
  const [userFilter, setUserFilter] = useState('')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState('7days')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [users, setUsers] = useState<any[]>([])

  useEffect(() => {
    loadData()
  }, [category, userFilter, sourceFilter, dateRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()

      if (category !== 'all') params.append('category', category)
      if (userFilter) params.append('user_id', userFilter)
      if (sourceFilter !== 'all') params.append('source', sourceFilter)

      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate)
        params.append('end_date', customEndDate)
      } else if (dateRange !== 'all') {
        const days = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90
        params.append('start_date', format(subDays(new Date(), days), 'yyyy-MM-dd'))
      }

      const [logsData, usersData] = await Promise.all([
        apiClient.get(`/api/audit-logs?${params.toString()}`),
        apiClient.get('/api/users')
      ])

      setLogs(logsData)
      setUsers(usersData.users || [])
    } catch (error) {
      console.error('Failed to load activity:', error)
      toast.error('Failed to load activity')
    } finally {
      setLoading(false)
    }
  }

  const handleExportLogs = () => {
    try {
      const csvContent = [
        ['Timestamp', 'User', 'Source', 'Activity', 'Category', 'Details', 'IP Address'].join(','),
        ...filteredLogs.map(log => [
          format(parseISO(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
          getActorName(log),
          log.source || '',
          renderSentence(log),
          log.entity_type || '',
          log.details ? JSON.stringify(log.details).replace(/"/g, '""') : '',
          log.ip_address || ''
        ].map(field => `"${field}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `activity-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Activity exported successfully')
    } catch {
      toast.error('Failed to export activity')
    }
  }

  const filteredLogs = logs.filter(log => {
    if (sourceFilter !== 'all') {
      const rowSource = log.source || ''
      if (sourceFilter === 'system') {
        if (!SYSTEM_SOURCES.has(rowSource)) return false
      } else if (rowSource !== sourceFilter) {
        return false
      }
    }
    if (!searchTerm) return true
    const sentence = renderSentence(log).toLowerCase()
    const actor = getActorName(log).toLowerCase()
    const term = searchTerm.toLowerCase()
    return sentence.includes(term) || actor.includes(term)
  })

  const dateGroups = groupByDate(filteredLogs)

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Activity</h2>
        <p className="text-gray-600">See what's happening across your family</p>
      </div>

      {/* Category Tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-4">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => setCategory(cat.value)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              category === cat.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search activity..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Everyone</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.display_name}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            {SOURCE_FILTERS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="custom">Custom Range</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <button
          onClick={handleExportLogs}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center text-sm"
        >
          <Download className="w-4 h-4 mr-1" />
          Export
        </button>
      </div>

      {dateRange === 'custom' && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>
      )}

      {/* Activity Feed */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : dateGroups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No activity found</p>
          <p className="text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-6">
          {dateGroups.map((group) => (
            <div key={group.label}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
                {group.label}
              </h3>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {group.logs.map(log => {
                  const style = getActionStyle(log.entity_type, log.action)
                  const sentence = renderSentence(log)
                  const actor = getActorName(log)
                  const isSystem = !log.user_id

                  return (
                    <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                      <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full ${style.bg} ${style.color} flex items-center justify-center`}>
                        {style.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">
                          {sentence}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center flex-wrap gap-x-2">
                          {isSystem ? (
                            <span className="inline-flex items-center">
                              <Zap className="w-3 h-3 mr-1" />
                              {actor}
                            </span>
                          ) : (
                            <span>by {actor}</span>
                          )}
                          {log.source && SOURCE_META[log.source] && (
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_META[log.source].bg} ${SOURCE_META[log.source].color}`}
                              title={`Action originated from ${SOURCE_META[log.source].label}`}
                            >
                              {SOURCE_META[log.source].icon}
                              {SOURCE_META[log.source].label}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-xs text-gray-400 mt-0.5">
                        {format(parseISO(log.created_at), 'h:mm a')}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredLogs.length >= 500 && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Showing most recent 500 entries. Use date filters to see older activity.
        </div>
      )}
    </div>
  )
}
